import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Alert, Select, Skeleton, Tabs } from 'antd';
import {
  AuditOutlined,
  CalculatorOutlined,
  DashboardOutlined,
  FileSearchOutlined,
  FilterOutlined,
  ShoppingOutlined,
  SettingOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { ContentBackground } from '@/components/content';
import { HowToCard } from '@/components/how-to-card';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import { DataTableSkeleton } from '@/components/skeletons';
import { ROLES_PERMISSIONS } from '@/constants';
import { QueryParamsProvider } from '@/contexts/query-params/QueryParamsProvider';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { API } from '@/services/api.service';
import CustomsTab from './_components/CustomsTab';
import FiscalBillsTab from './_components/FiscalBillsTab';
import InputVatTab from './_components/InputVatTab';
import StatusTab from './_components/StatusTab';
import VatRecordsTab from './_components/VatRecordsTab';
import { str } from './_components/helpers';

const DEFAULT_PARAMS = Object.freeze({ offset: '1', limit: '50' });
const HOW_TO_ICONS = [SettingOutlined, FilterOutlined, SwapOutlined, FileSearchOutlined];
const TABS = [
  { key: 'customs', icon: <AuditOutlined />, Component: CustomsTab },
  { key: 'inputVat', icon: <CalculatorOutlined />, Component: InputVatTab },
  { key: 'vatRecording', icon: <FileSearchOutlined />, Component: VatRecordsTab },
  { key: 'fiscalBills', icon: <ShoppingOutlined />, Component: FiscalBillsTab },
];
// Filters and sorts of one tab mean nothing on another.
const keepOnTabChange = (queryParams) =>
  Object.fromEntries(Object.keys(queryParams).filter((k) => !['company', 'limit'].includes(k)).map((k) => [k, undefined]));
/**
 * „Podaci sa SEF-a" under Invoices: what the state holds about the company —
 * import customs declarations (JCI), input VAT compared with the books in
 * AB, VAT calculation records (PEP), received fiscal bills. Read every
 * morning from SEF and ppppdv with the company's key, module by module as
 * switched on in the company settings. Company, tab and filters live in the
 * address.
 *
 * @returns {JSX.Element}
 */
function SefStateContent() {
  const { t } = useTranslation(['sefState', 'menus']);
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const company = str(queryParams.company);

  const { data: companies = [], isLoading: companiesLoading } = useQuery({
    queryKey: ['sefState', 'companies'],
    queryFn: () => API.sefState.getCompanies(),
  });
  useEffect(() => {
    if (!company && companies.length === 1) setQueryParams({ company: String(companies[0]._id), offset: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, company]);

  const profile = useQuery({
    queryKey: ['sefState', company, 'profile'],
    queryFn: () => API.sefState.getProfile(company),
    enabled: Boolean(company),
    retry: false,
  });

  // A tab shows when its module is on or already holds data.
  const visible = TABS.filter((tb) => profile.data?.switches?.[tb.key] || profile.data?.modules?.[tb.key]?.count > 0);
  const requested = str(queryParams.tab);
  const tab = requested === 'status' || visible.some((tb) => tb.key === requested) ? requested : visible[0]?.key || 'status';
  const showCompanySelect = companies.length > 1;
  const howToSteps = t('howTo.steps', { returnObjects: true });

  return (
    <RootLayout metadata={{ title: t('sefState', { ns: 'menus' }) }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />
      <Section>
        <ContainerFluid className="foreground-wrapper">
          <div className="rounded-lg bg-white p-4">
            <p className="mb-3 text-sm opacity-70">{t('subtitle')}</p>
            <HowToCard
              title={t('howTo.title')}
              sections={[
                {
                  title: t('howTo.section'),
                  steps: (Array.isArray(howToSteps) ? howToSteps : []).map((s, i) => ({ ...s, icon: HOW_TO_ICONS[i] })),
                },
              ]}
            />
            {companiesLoading && <Skeleton.Input active className="mb-3 !w-[420px] max-w-full" />}
            {showCompanySelect && (
              <Select
                showSearch
                optionFilterProp="label"
                className="mb-3 w-[420px] max-w-full"
                popupMatchSelectWidth={false}
                placeholder={t('company')}
                value={company || undefined}
                onChange={(v) => setQueryParams({ ...keepOnTabChange(queryParams), company: v, offset: 1 })}
                options={companies.map((c) => ({ value: String(c._id), label: c.name }))}
              />
            )}
            {!companiesLoading && !companies.length && <Alert type="info" showIcon message={t('noCompanies')} />}
            {companies.length > 0 && !company && <Alert type="info" showIcon message={t('pickCompany')} />}

            {company && profile.isLoading && (
              <div className="mt-2">
                <Skeleton.Input active size="small" className="mb-3 !w-[480px] max-w-full" />
                <DataTableSkeleton columnLength={6} />
              </div>
            )}
            {company && profile.error && <Alert type="error" showIcon message={t('errors.load')} />}

            {company && profile.data && (
              <Tabs
                activeKey={tab}
                destroyInactiveTabPane
                onChange={(v) => setQueryParams({ ...keepOnTabChange(queryParams), tab: v, offset: 1 })}
                items={[
                  ...visible.map(({ key, icon, Component }) => ({
                    key,
                    icon,
                    label: `${t(`tabs.${key}`)}${profile.data.modules[key]?.count ? ` · ${profile.data.modules[key].count}` : ''}`,
                    children: <Component company={company} queryParams={queryParams} setQueryParams={setQueryParams} />,
                  })),
                  {
                    key: 'status',
                    icon: <DashboardOutlined />,
                    label: t('tabs.status'),
                    children: <StatusTab company={company} profile={profile.data} />,
                  },
                ]}
              />
            )}
          </div>
        </ContainerFluid>
      </Section>
    </RootLayout>
  );
}

function SefStateWithParams() {
  return (
    <QueryParamsProvider defaultParams={DEFAULT_PARAMS}>
      <SefStateContent />
    </QueryParamsProvider>
  );
}

const SefStatePage = withPrivateRoute(SefStateWithParams, ROLES_PERMISSIONS.SEF_STATE.VIEW);
SefStatePage.displayName = 'SefStatePage';

export default SefStatePage;
